import React, { useMemo, useState } from 'react';
import {
  CopyMode,
  FormFieldConfig,
  Mold,
  MoldOrderFirstLevelType,
  OrderDetail,
} from '../../types/mold';
import StickyBottomBar from './StickyBottomBar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './CustomTabs';
import { formatFieldValue } from './formConfigs';
import { useMoldOrders } from '../../hooks/useMoldOrders';
import { useAppState } from '../../context/AppStateContext';
import { AlertTriangle, CheckCircle2, ExternalLink, RotateCcw } from 'lucide-react';

export interface CreatedOrderResult {
  moldId: string;
  orderId: string;
  details: OrderDetail;
  mold: Mold;
}

interface FailedOrderResult {
  moldId: string;
  mold: Mold;
  reason: string;
}

interface BatchSubmissionResult {
  successes: CreatedOrderResult[];
  failures: FailedOrderResult[];
}

const getBatchCreateFailureReason = (error: unknown): string => {
  const response = (error as {
    response?: { status?: number; data?: { message?: string; error?: string } };
  })?.response;
  const apiReason = response?.data?.message || response?.data?.error;
  if (apiReason) return apiReason;

  if (response?.status === 409) {
    return 'แม่พิมพ์มีใบงานที่กำลังดำเนินการอยู่แล้ว';
  }
  if (response?.status === 400 || response?.status === 422) {
    return 'ข้อมูลใบงานไม่ถูกต้องหรือไม่ครบถ้วน';
  }
  if (response?.status === 403) {
    return 'ไม่มีสิทธิ์สร้างใบงานนี้';
  }
  if (response?.status && response.status >= 500) {
    return 'ระบบไม่สามารถสร้างใบงานได้ กรุณาลองใหม่อีกครั้ง';
  }
  if (error instanceof Error && error.message) return error.message;
  return 'ไม่สามารถสร้างใบงานได้โดยไม่ทราบสาเหตุ';
};

const ReviewField: React.FC<{
  config: FormFieldConfig;
  value: any;
}> = ({ config, value }) => {
  const formattedValue = formatFieldValue(config, value);
  if (!formattedValue) return null;

  return (
    <div className='grid gap-2' data-cy={`review-${String(config.key)}`}>
      <label className='text-sm font-medium text-gray-700'>
        {config.label}
      </label>
      <div className='w-full bg-gray-50 rounded-lg py-2.5 px-3 text-gray-900 text-sm'>
        {formattedValue}
      </div>
    </div>
  );
};

interface MoldReviewContentProps {
  mold: Mold;
  details: OrderDetail;
  parentOrderId?: number | null;
  moldOrderFirstLevelType?: MoldOrderFirstLevelType;
}
const MoldReviewContent: React.FC<MoldReviewContentProps> = ({
  mold,
  details,
  parentOrderId,
  moldOrderFirstLevelType = 'maintenance',
}) => {
  const { getFormFields } = useAppState();
  return (
    <div className='max-w-4xl mx-auto'>
      <div className='bg-white rounded-lg border border-gray-200'>
        <div className='p-6'>
          <div className='flex justify-between items-center mb-6 pb-4 border-b border-gray-100'>
            <div>
              <h3 className='text-xl font-semibold text-gray-900'>
                แม่พิมพ์ {mold.sbiId}
              </h3>
              <p className='mt-1 text-sm text-gray-600'>
                ชิ้นส่วนที่เกี่ยวข้อง:{' '}
                {mold.productParts.map((p) => p.partNumber).join(', ')}
              </p>
              {parentOrderId && (
                <p className='mt-4 text-sm text-gray-600'>
                  สร้างจากใบงานรหัส #{parentOrderId}
                </p>
              )}
            </div>
          </div>

          <div className='space-y-6'>
            {getFormFields(moldOrderFirstLevelType).map((field) => {
              return (
                (!field.showIf || field.showIf(details)) && (
                  <ReviewField
                    key={String(field.key)}
                    config={field}
                    value={details[field.key as keyof OrderDetail]}
                  />
                )
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

interface MoldOrderReviewProps {
  molds: Mold[];
  orders: Record<string, OrderDetail>;
  onPrevStep: () => void;
  onOrderSuccess: (
    orders: Array<{
      moldId: string;
      orderId: string;
      details: OrderDetail;
      mold: Mold;
    }>,
  ) => void;
  parentOrderId?: number | null;
  moldOrderFirstLevelType: MoldOrderFirstLevelType;
  isSubOrder?: boolean;
  /** When 'all', tell the backend to clone the parent's form-attachment images onto the
   *  new order (photos are questionnaire data, carried only for a full copy). */
  copyMode?: CopyMode;
  previouslyCreatedOrders?: CreatedOrderResult[];
  onPartialSuccess?: (orders: CreatedOrderResult[]) => void;
}

const MoldOrderReview: React.FC<MoldOrderReviewProps> = ({
  molds,
  orders,
  onPrevStep,
  onOrderSuccess,
  parentOrderId,
  moldOrderFirstLevelType,
  isSubOrder = false,
  copyMode = 'none',
  previouslyCreatedOrders = [],
  onPartialSuccess,
}) => {
  const [activeTab, setActiveTab] = useState(molds[0]?.id || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState<BatchSubmissionResult | null>(
    previouslyCreatedOrders.length > 0
      ? { successes: previouslyCreatedOrders, failures: [] }
      : null,
  );
  const { createMoldOrder } = useMoldOrders();
  const { getCurrentOrderTemplateVersion } = useAppState();
  const formVersion = getCurrentOrderTemplateVersion(moldOrderFirstLevelType);
  const successfulMoldIds = useMemo(
    () => new Set(submissionResult?.successes.map((item) => item.moldId) ?? []),
    [submissionResult?.successes],
  );
  const pendingMolds = molds.filter((mold) => !successfulMoldIds.has(mold.id));

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const attemptResults = await Promise.all(
        pendingMolds.map(async (mold) => {
          const { priority, ...orderDetail } = orders[mold.id];
          const orderData = {
            moldId: mold.id,
            type: orderDetail.moldOrderType,
            description: orderDetail.note,
            orderDetail: {
              ...orderDetail,
              formVersion,
            },
            plannedCompletionDate: orderDetail.dueDate,
            priority: priority ?? 200,
            parentOrderId: parentOrderId ? parentOrderId : undefined,
            isSubOrder,
            // Opt-in copy: the backend clones the parent's form-attachment images onto
            // the new order (true byte-copy, same-mold/company guarded server-side).
            copyFilesFromOrderId:
              copyMode === 'all' && parentOrderId ? parentOrderId : undefined,
          };

          try {
            const result = await createMoldOrder(orderData);
            return {
              status: 'fulfilled' as const,
              value: {
                moldId: mold.id,
                orderId: result.id.toString(),
                details: orderDetail,
                mold,
              },
            };
          } catch (error) {
            return {
              status: 'rejected' as const,
              value: {
                moldId: mold.id,
                mold,
                reason: getBatchCreateFailureReason(error),
              },
            };
          }
        }),
      );

      const newSuccesses = attemptResults
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value);
      const failures = attemptResults
        .filter((result) => result.status === 'rejected')
        .map((result) => result.value);
      const combinedSuccesses = [
        ...(submissionResult?.successes ?? []),
        ...newSuccesses,
      ];
      const nextResult = { successes: combinedSuccesses, failures };

      if (failures.length === 0 && combinedSuccesses.length === molds.length) {
        onOrderSuccess(combinedSuccesses);
      } else {
        setSubmissionResult(nextResult);
        if (newSuccesses.length > 0) onPartialSuccess?.(combinedSuccesses);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='pb-24'>
      {submissionResult && (
        <section
          className='mx-auto mb-4 max-w-5xl rounded-xl border border-gray-200 bg-white p-4 shadow-sm'
          data-testid='batch-submission-result'
          aria-live='polite'
        >
          <div className='flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between'>
            <div>
              <h2 className='text-lg font-semibold text-gray-900'>ผลการสร้างใบงานแบบกลุ่ม</h2>
              <p className='mt-1 text-sm text-gray-600'>
                สำเร็จ {submissionResult.successes.length} จาก {molds.length} รายการ
                {submissionResult.failures.length > 0 && (
                  <> · ไม่สำเร็จ {submissionResult.failures.length} รายการ</>
                )}
              </p>
              {submissionResult.successes.length > 0 && submissionResult.failures.length === 0 && (
                <p className='mt-1 text-xs text-blue-700'>
                  แก้ไขข้อมูลรายการที่เหลือแล้วกดสร้างใบงานอีกครั้งได้ รายการที่สำเร็จจะไม่ถูกส่งซ้ำ
                </p>
              )}
            </div>
            {submissionResult.failures.length > 0 && (
              <button
                type='button'
                onClick={handleSubmit}
                disabled={isSubmitting}
                className='inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300'
              >
                <RotateCcw className='h-4 w-4' />
                {isSubmitting
                  ? 'กำลังลองใหม่'
                  : `ลองเฉพาะ ${submissionResult.failures.length} รายการที่ไม่สำเร็จ`}
              </button>
            )}
          </div>

          {submissionResult.successes.length > 0 && (
            <div className='mt-4 rounded-lg border border-green-200 bg-green-50 p-3'>
              <h3 className='flex items-center gap-2 text-sm font-semibold text-green-900'>
                <CheckCircle2 className='h-4 w-4' /> สร้างสำเร็จ
              </h3>
              <ul className='mt-2 divide-y divide-green-200'>
                {submissionResult.successes.map((item) => (
                  <li key={item.moldId} className='flex items-center justify-between gap-3 py-2 text-sm'>
                    <span className='text-green-900'>แม่พิมพ์ {item.mold.sbiId}</span>
                    <a
                      href={`/mold-orders/${item.orderId}`}
                      className='inline-flex items-center gap-1 font-semibold text-green-800 underline hover:text-green-950'
                    >
                      ใบงาน #{item.orderId} <ExternalLink className='h-3.5 w-3.5' />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {submissionResult.failures.length > 0 && (
            <div className='mt-3 rounded-lg border border-red-200 bg-red-50 p-3'>
              <h3 className='flex items-center gap-2 text-sm font-semibold text-red-900'>
                <AlertTriangle className='h-4 w-4' /> สร้างไม่สำเร็จ
              </h3>
              <ul className='mt-2 divide-y divide-red-200'>
                {submissionResult.failures.map((item) => (
                  <li key={item.moldId} className='py-2 text-sm'>
                    <p className='font-semibold text-red-900'>แม่พิมพ์ {item.mold.sbiId}</p>
                    <p className='mt-0.5 text-red-800'>{item.reason}</p>
                  </li>
                ))}
              </ul>
              {submissionResult.successes.length > 0 && (
                <p className='mt-2 text-xs font-medium text-red-800'>
                  ใบงานที่สำเร็จแล้วจะไม่ถูกส่งซ้ำเมื่อกดลองใหม่
                </p>
              )}
            </div>
          )}
        </section>
      )}
      <Tabs value={activeTab} onChange={setActiveTab} className='w-full'>
        <div className='bg-white border-b border-gray-200'>
          <div className='mx-auto'>
            <TabsList>
              {molds.map((mold) => (
                <TabsTrigger
                  key={mold.id}
                  value={mold.id}
                  currentValue={activeTab}
                  onClick={() => setActiveTab(mold.id)}
                >
                  แม่พิมพ์ {mold.sbiId}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </div>

        {molds.map((mold) => (
          <TabsContent key={mold.id} value={mold.id} currentValue={activeTab}>
            <MoldReviewContent
              mold={mold}
              details={orders[mold.id]}
              moldOrderFirstLevelType={moldOrderFirstLevelType}
              parentOrderId={parentOrderId}
            />
          </TabsContent>
        ))}
      </Tabs>

      <StickyBottomBar
        prevLabel='แก้ไข'
        nextLabel={
          isSubmitting
            ? 'กำลังสร้างใบงาน'
            : submissionResult?.failures.length
              ? `ลองใหม่เฉพาะที่ไม่สำเร็จ (${submissionResult.failures.length})`
              : submissionResult?.successes.length
                ? `สร้างใบงานที่เหลือ (${pendingMolds.length})`
              : 'สร้างใบงาน'
        }
        onPrevStep={onPrevStep}
        onNextStep={handleSubmit}
        disableNext={isSubmitting || pendingMolds.length === 0}
      />
    </div>
  );
};

export default MoldOrderReview;
